import { VITAL_SIGNS_CLINICAL_NON_CLINICAL } from '@constants/extension-codes';
import { COMPLETE_LAB_OBSERVATION_CODE_MAP } from '@constants/option-types/lab';

import { formatFhirDate, getFhirDate } from '@utils/fhir';
import { capitalizeAll } from '@utils/format';

import {
  type ObservationComponent,
  type ObservationModel,
  type ObservationModelInput,
  type Maybe,
  ResourceSourceType,
  type ObservationAlertModel,
} from 'generated/types';

import { FhirResource } from './fhir-resource';

export type ReducedObservationComponent = {
  code: string;
  value: number | null;
  unit: string | null | undefined;
  display: string;
  units: { k: string | undefined; v: string | undefined }[] | undefined;
  isSingleUnit?: boolean;
};

export type ObservationValues = Pick<
  ObservationRecord,
  'clinicalStatus' | 'code' | 'selfReported' | 'source' | 'testDate'
> & {
  // displayName
  title: string;
};
export class ObservationRecord extends FhirResource {
  protected model: ObservationModel;

  public constructor(model: ObservationModel) {
    super(model);
    this.model = model;
  }

  public get value(): number[] | boolean | number | string | null {
    const { component, valueInteger, valueQuantity } = this.model;
    if (typeof valueInteger === 'number') {
      return valueInteger;
    }
    if (component) {
      return component
        .map((c) => c?.valueQuantity?.value)
        .filter((v): v is number => v !== null);
    }
    return valueQuantity?.value ?? null;
  }

  public get unit() {
    const { component, valueQuantity } = this.model;
    if (component) {
      const definedComponent = component.find((c): c is ObservationComponent =>
        Boolean(c)
      );
      if (definedComponent) {
        return definedComponent.valueQuantity?.unit;
      }
    }
    return valueQuantity?.unit;
  }

  public get testDate() {
    return this.model.effectiveDateTime;
  }

  public set testDate(value: string | null | undefined) {
    this.model = {
      ...this.model,
      effectiveDateTime: value ?? null,
    };
  }

  public get category() {
    const codes = this.model.category?.reduce<string[]>((acc, curr) => {
      const values = curr.coding?.map(({ code }) => code);
      if (values) {
        for (const value of values) {
          if (value) {
            acc.push(value);
          }
        }
      }
      return acc;
    }, []);
    return codes?.join(',') ?? undefined;
  }

  /**
   * VITALL custom code that identifies the type of the observation
   */
  public get code(): string {
    const coding = this.model.code.coding?.find(({ system }) =>
      system?.startsWith('https://vitall.com')
    );
    return coding?.code ?? 'Unknown';
  }

  public set code(value: string | null | undefined) {
    // Try to find code in list
    const preexistingValue = value
      ? COMPLETE_LAB_OBSERVATION_CODE_MAP[value]
      : null;

    const defaultDisplay = capitalizeAll(value?.replace(/\W/, ' ') ?? '');
    this.model = {
      ...this.model,
      code: value
        ? {
            coding: [
              {
                code: value,
                display: preexistingValue ?? defaultDisplay,
                system: ObservationRecord.VITALL_SYSTEM,
              },
            ],
            text: preexistingValue ?? defaultDisplay,
          }
        : {},
    };
  }

  public get displayName() {
    return (
      this.model.code.text ??
      this.model.code.coding?.map((c) => c.display).join(',')
    );
  }

  public set displayName(value: string | null | undefined) {
    this.model = {
      ...this.model,
      code: value
        ? {
            coding: [
              {
                code: this.code,
                display: value,
                system: ObservationRecord.VITALL_SYSTEM,
              },
            ],
            text: value,
          }
        : {},
    };
  }

  public get encounter() {
    return this.model.encounter?.reference ?? 'Unknown';
  }

  public get source() {
    return (
      this.model.device?.display ??
      this.model.performer?.map((p) => p?.display).join('/')
    );
  }

  public get valueQuantity() {
    return this.model.valueQuantity?.value ?? null;
  }

  public get component() {
    return this.model.component;
  }

  public get clinicalStatus() {
    const extension = this.findExtension(VITAL_SIGNS_CLINICAL_NON_CLINICAL);
    return extension?.valueBoolean ?? false;
  }

  public set clinicalStatus(value: boolean) {
    this.addOrReplaceExtension({
      url: VITAL_SIGNS_CLINICAL_NON_CLINICAL,
      valueBoolean: value,
    });
  }

  public get sourceType(): ResourceSourceType | undefined {
    const isDevice = this.hasCategory('device-data');
    const isScanData = this.hasCategory('scan-data');
    return (
      this.recordSource ??
      (isDevice
        ? ResourceSourceType.device
        : isScanData
          ? ResourceSourceType.scan
          : undefined)
    );
  }

  public get interpretation() {
    const [range] = this.model.interpretation ?? [];
    return range?.text ?? undefined;
  }

  public get alert(): ObservationAlertModel | null | undefined {
    return this.model.alert;
  }

  protected static roundValue(
    value: number | string | null | undefined
  ): number | null {
    if (value === null || value === undefined) {
      return null;
    }
    let parsed = value;
    if (typeof parsed === 'string') {
      try {
        parsed = parseFloat(parsed);
      } catch {
        return null;
      }
    }
    return Math.round(parsed * 100) / 100;
  }

  public setCode(code: string, display: string, system?: string) {
    this.model = {
      ...this.model,
      code: {
        coding: [
          {
            code,
            display,
            system: system ?? ObservationRecord.VITALL_SYSTEM,
          },
        ],
        text: display,
      },
    };
  }

  public hasCode(codeId: string) {
    return (
      this.model.code.coding?.some((c) => c.code === codeId) ||
      this.model.component?.some((c) =>
        c?.code.coding?.some((o) => o.code === codeId)
      )
    );
  }

  public hasCategory(codeId: string) {
    return this.model.category?.some((c) =>
      c.coding?.some((o) => o.code === codeId)
    );
  }

  public isClinical() {
    const extension = this.findExtension(VITAL_SIGNS_CLINICAL_NON_CLINICAL);
    return extension?.valueBoolean ?? false;
  }

  public getObservationDate() {
    const isDeviceData =
      this.hasCategory('device-data') || this.hasCategory('scan-data');
    const date =
      this.model.effectiveDateTime ?? this.model.effectivePeriod?.end;
    return getFhirDate(date, {
      adjustTz: isDeviceData,
    });
  }

  public getObservationDateStr() {
    const isDeviceData =
      this.hasCategory('device-data') || this.hasCategory('scan-data');
    const date =
      this.model.effectiveDateTime ?? this.model.effectivePeriod?.end;
    return formatFhirDate(date, {
      showTime: false,
      adjustTz: isDeviceData,
    });
  }

  public getTestDate() {
    return getFhirDate(this.testDate);
  }

  public getTestDateStr(showTime = false) {
    showTime = showTime && !this.testDate?.endsWith('T00:00:00.000Z');
    return formatFhirDate(this.testDate, { showTime });
  }

  public getModelInput(values?: ObservationValues): ObservationModelInput {
    if (values) {
      // eslint-disable-next-line guard-for-in
      for (const key in values) {
        try {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          this[key] = values[key];
        } catch {
          // eslint-disable-next-line no-continue
          continue;
        }
      }
      this.code = values.title;
    }
    const cleaned = FhirResource.getCleanInput(this.model);
    return cleaned;
  }

  protected getComponents(): ReducedObservationComponent[] {
    const component = this.model.component;
    const valueQuantity = this.model.valueQuantity;
    if (component) {
      return component
        .map<Partial<ReducedObservationComponent>>((c) => {
          const coding = c?.code.coding?.at(0);
          return {
            value:
              ObservationRecord.roundValue(c?.valueQuantity?.value) ??
              undefined,
            code: coding?.code ?? undefined,
            display: coding?.display ?? undefined,
            unit: c?.valueQuantity?.unit ?? undefined,
            units: [],
          };
        })
        .filter(
          (m): m is ReducedObservationComponent =>
            Boolean(m.code) && m.value !== null
        );
    }
    return [
      {
        value: valueQuantity?.value ?? 0,
        code: this.code,
        unit: valueQuantity?.unit ?? '',
        units: [],
        display: this.displayName ?? '',
        isSingleUnit: false,
      },
    ];
  }

  protected setComponents(measurements: ReducedObservationComponent[]) {
    if (measurements.length === 1) {
      const [{ unit, value }] = measurements;
      this.model = {
        ...this.model,
        valueQuantity: {
          ...this.model.valueQuantity,
          unit,
          value,
        },
      };
    } else {
      let { component } = this.model;
      // Patch component with updates
      component = component?.map<Maybe<ObservationComponent>>((c) => {
        if (!c) {
          return null;
        }
        const coding = c.code.coding?.at(0);
        const updated = measurements.find(({ code }) => code === coding?.code);
        if (updated) {
          return {
            ...c,
            valueQuantity: {
              ...c.valueQuantity,
              value: updated.value ?? c.valueQuantity?.value,
              unit: updated.unit ?? c.valueQuantity?.unit,
            },
          };
        }
        return c;
      });
      this.model = {
        ...this.model,
        component,
      };
    }
  }
}
